// TODDLER + YOUNG CHILD — 18 months+
// Sleep + development + routines + boundaries + independence.

export const toddler = {
  id: "2-4",
  num: "04",
  unit: "18 months +",
  name: "Toddler + Young Child",
  img: "https://images.unsplash.com/photo-1530455623052-e8441ae826f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHx0b2RkbGVyJTIwYmVkdGltZSUyMHJvdXRpbmV8ZW58MHx8fHwxNzg3OTM5NjYzfDA&ixlib=rb-4.1.0&q=85&w=900",
  alt: "A young child finally asleep after a long bedtime",
  titleLines: [
    { t: "At some point," },
    { t: "bedtime stopped" },
    { t: "being bedtime.", em: true },
    { t: "It became a negotiation.", em: true },
  ],
  resourceTitle: "Toddler & Young Child Sleep Guide",
  resourceDescriptor: "The bedtime that became a negotiation, the footsteps after you leave the room — and what actually helps.",
  introFragments: [
    "One more book.",
    "One more drink.",
    "One more hug.",
    "Bathroom again.",
    "Wrong blanket. Door open. No — door closed.",
    "You finally leave. Thirty seconds later: footsteps.",
  ],
  introAfter: "Toddler sleep brings something new into the picture. Your child can now participate very actively in whether bedtime happens.",
  topics: [
    {
      id: "fights-bedtime",
      q: "Why does my toddler fight bedtime?",
      body: [
        "Because bedtime now sits at the intersection of everything toddler. Their sleep timing, their growing independence, their brand-new ability to test limits, their awareness that you exist somewhere without them, the day\u2019s transitions, and — underneath it all — whether their body is actually ready for sleep.",
        "That\u2019s why \u2018bedtime battle\u2019 rarely means one thing. A child who isn\u2019t tired yet will resist. A child who is overtired will resist differently. A child testing what happens when they say \u2018one more\u2019 is running a different exercise entirely than a child anxious about the separation.",
        "Reducing every bedtime fight to \u2018bad behavior\u2019 misses most of the picture — and responding to the wrong driver is how bedtime gets longer instead of shorter.",
      ],
    },
    {
      id: "getting-out-of-bed",
      q: "Why does my toddler keep getting out of bed?",
      body: [
        "Because it works — at least as an experiment. Your toddler has discovered that leaving the bed produces a response, and children are meticulous scientists of cause and effect. The question isn\u2019t why they test it; it\u2019s what the test keeps returning.",
        "Two different things can be happening. A sleep timing issue: the child isn\u2019t actually sleepy, and the bed feels like a cage. Or an interaction pattern: the child is sleepy, but the sequence — out of bed, parent appears, walk back, repeat — has become the predictable evening ritual.",
        "Telling them apart starts with observation. What happens immediately before they leave the bed? What happens when they come out? What response do they get — and does the same sequence repeat? Is the child actually tired? And has anything at home changed recently?",
      ],
      observe: [
        "What happens right before they get out of bed?",
        "What response do they receive when they come out?",
        "Does the same sequence repeat, night after night?",
        "Is the child genuinely sleepy at bedtime?",
      ],
    },
    {
      id: "one-more-everything",
      q: "How do I handle the \u2018one more everything\u2019 bedtime?",
      body: [
        "One more story. One more drink. One more hug. One more bathroom trip. One more question. One more reason you cannot possibly leave the room yet.",
        "What you\u2019re watching is bedtime becoming a predictable interaction loop. Your child isn\u2019t being manipulative — they\u2019ve simply learned that bedtime is a sequence with many steps, and every request has historically extended it. The routine has quietly grown to include the negotiating.",
        "The way out usually isn\u2019t a crackdown; it\u2019s clarity. A bedtime sequence that is complete, predictable and lovingly finite gives a toddler something more valuable than an extra story: knowledge of exactly what happens next, and what happens last.",
      ],
    },
    {
      id: "needs-me-in-room",
      q: "Why does my toddler need me in the room to fall asleep?",
      body: [
        "For the same reason any of us sleeps better in familiar conditions: your presence has become part of how sleep happens. When something is present every time a child drifts off, it becomes part of the expected environment — as ordinary to them as the pillow.",
        "Then the honest fork. \u2018This works for our family\u2019 is a legitimate place to be — many parents sit with their child and genuinely don\u2019t mind. \u2018I want my child to fall asleep without me sitting here every night\u2019 is equally legitimate, and it\u2019s a different project.",
        "If you\u2019re in the second camp, the change is usually gradual — your presence slowly becoming less central to the falling-asleep process, at a pace your child can absorb. What it never needs to be is abrupt or cold. The parent\u2019s goal sets the course; the child\u2019s temperament sets the speed.",
      ],
    },
    {
      id: "waking-at-night",
      q: "Why is my toddler waking at night?",
      body: [
        "Everyone wakes at night — toddlers included. The question is what happens next: whether your child resettles on their own, or whether the waking starts a sequence that involves you.",
        "The contexts vary widely. Bedtime that\u2019s off, naps that shifted, a routine that changed, the sleep environment, the conditions they fell asleep under, a developmental surge, how wakings have been responded to, the schedule as a whole, or a temporary disruption like illness or travel.",
        "Because the list is long, the waking itself tells you little without its surroundings. When does it happen? What does your child ask for? What resolves it? Is it the same every night? A single wake-up is an event. A repeated pattern is a message.",
      ],
    },
    {
      id: "comes-into-my-room",
      q: "Why does my toddler come into my room every night?",
      body: [
        "Because it has become the pattern — and patterns this comforting don\u2019t dissolve on their own. Your child makes the trip, finds you, and something reassuring happens. From their side of the hallway, the system works perfectly.",
        "Understanding your specific pattern starts with the details: what time it happens, what your child asks for, how you respond, where they end up sleeping, whether it\u2019s consistent, and — often most revealing — what changed just before it began.",
        "And to be clear: plenty of families welcome nighttime visits, and shared sleep arrangements are a family\u2019s own call. This only becomes a problem to solve when it stops working for the people in it — including you.",
      ],
    },
    {
      id: "waking-at-5am",
      q: "Why is my toddler waking at 5 a.m.?",
      body: [
        "It\u2019s 5:04 a.m. Apparently we\u2019re starting the day.",
        "Early waking is stubborn because of where it sits in the night. By morning, sleep pressure — the biological drive that keeps a child asleep — has mostly burned off. The lightest sleep of the night meets the first light of the day, and anything slightly off can end the night early.",
        "That\u2019s why 5 a.m. rarely answers to a single fix. Bedtime, nap timing and length, the room environment, the established pattern of what happens at that waking, and simple individual variation all play in. The whole picture has to be read before any one piece gets adjusted — and even then, some children are simply larks for a while.",
      ],
    },
    {
      id: "nap-vs-bedtime",
      q: "Is the nap causing the bedtime problems?",
      body: [
        "Welcome to the classic toddler trap. They nap: bedtime becomes impossible. They don\u2019t nap: they\u2019re falling apart before dinner. Both outcomes feel like failure, and the nap sits at the center of it.",
        "What\u2019s usually happening is a shift in sleep needs. As children grow, the sleep they need redistributes — and the nap is where the pressure shows first. A nap that runs long or late can leave too little sleep pressure at bedtime; dropping it too early can produce an overtired, wired evening.",
        "The answer isn\u2019t in the nap alone but in the balance — how much daytime sleep, when it ends, and what bedtime looks like in response. Watching that balance across a week tells you far more than any single impossible Tuesday.",
      ],
    },
    {
      id: "dropping-the-nap",
      q: "How do I know if my toddler is ready to drop the nap?",
      body: [
        "Slowly — that\u2019s the honest answer. One refused nap is a day. A pattern of refused naps, or naps that happen but push bedtime hours later, repeated over a couple of weeks, is a signal.",
        "Transitions like this are messy before they\u2019re consistent. Many children go through a stretch of alternating — nap some days, none on others — before the new pattern holds. Judging readiness on any single day leads to whiplash; watching the trend leads to timing.",
        "During the transition itself, an earlier bedtime on no-nap days often carries the family through. The messiness isn\u2019t a sign you\u2019re doing it wrong; it\u2019s what the middle of a change looks like.",
      ],
    },
    {
      id: "crib-to-bed",
      q: "What about the crib-to-bed transition?",
      body: [
        "Moving from a crib to a bed changes more than furniture — it changes the behavioral environment completely. A crib makes the decision for the child. A bed hands the decision to a person who has very strong opinions and newfound freedom.",
        "That\u2019s why timing matters, and why the transition often goes better when it\u2019s not rushed by a deadline. The room should be fully safe for a child who can now move around it unsupervised — furniture anchored, hazards removed, the door situation thought through. Restraint or confinement tricks are never the answer; safety and clarity are.",
        "Expectations help enormously. A child who knows the routine, knows what happens after lights-out, and meets the same calm response every time they test the new freedom, adjusts far faster than one navigating an unpredictable system.",
      ],
    },
    {
      id: "after-travel-illness",
      q: "Why did sleep fall apart after travel, illness or a big change?",
      body: [
        "Because sleep lives inside routine, and life just took the routine apart. Travel rearranges environments and time zones. Illness rearranges everything — a sick child needs comfort, extra contact, maybe extra feeds, and those nights of necessary deviation can linger after the fever is gone.",
        "The way back is usually familiar, not forceful. Returning to the cues and sequences your child already knows — the same wind-down, the same expectations, the same calm responses — re-anchors the pattern. Most disruptions unwind within days to weeks of consistency.",
        "One caveat that matters: never \u2018sleep train through\u2019 illness. A sick or uncomfortable child needs care first. The behavioral work waits for the healthy child to return.",
      ],
    },
    {
      id: "does-toddler-need-sleep-training",
      q: "Does my toddler need \u2018sleep training\u2019?",
      body: [
        "Probably not in the way the phrase makes you picture. Toddler sleep is a different animal from infant sleep training, because the challenges are different: sleep timing, bedtime routines, boundaries, your presence at bedtime, nighttime responses, nap transitions, independence, and behavior patterns that a thinking, remembering, negotiating child actively participates in.",
        "What helps a toddler is rarely a single method — it\u2019s a coherent picture. When sleep happens, what happens around it, what the child has learned to expect, and whether the family\u2019s responses are consistent enough to be believed.",
        "So the better question isn\u2019t \u2018does my toddler need sleep training\u2019 — it\u2019s \u2018which part of our sleep picture is driving this.\u2019 That\u2019s a smaller, kinder, more answerable question.",
      ],
    },
    {
      id: "consistency",
      q: "Why does consistency matter — and what does it actually mean?",
      body: [
        "Consistency gets quoted so often it\u2019s lost its meaning, so here\u2019s the useful version: children learn what to expect from what repeatedly happens. When bedtime means the same sequence, and the same response follows the same behavior, a child stops needing to test the system every night to find out.",
        "Consistency is not robotic perfection. It doesn\u2019t mean never deviating — illness, travel, family events and plain hard nights all legitimately bend the pattern. Life happens; the pattern survives it.",
        "The distinction worth holding: consistency is about the shape of the routine and the predictability of your responses, not about flawless execution. A family that returns to its shape after disruptions is consistent in every way that matters.",
      ],
    },
    {
      id: "pediatrician",
      q: "When should this go to the pediatrician?",
      body: [
        "The boundary is the same at every age: sleep support handles behavior, routine, timing and rhythm; health belongs with your child\u2019s healthcare professional.",
        "Loud or labored breathing during sleep, concerns about growth, illness, unusual symptoms, or anything that feels medical — those go to the pediatrician. Once health is settled, the behavioral and schedule side of sleep is exactly what remains — and exactly what can be worked on.",
      ],
    },
  ],
  checklist: {
    title: ["My child…"],
    items: [
      "takes forever to fall asleep",
      "repeatedly gets out of bed",
      "needs me in the room",
      "comes into my room overnight",
      "wakes very early",
      "suddenly refuses naps",
      "naps and then won\u2019t sleep at bedtime",
      "asks for one more thing indefinitely",
      "used to sleep well and suddenly doesn\u2019t",
      "recently moved from crib to bed",
      "has sleep problems after travel or illness",
      "has turned bedtime into the hardest part of our day",
    ],
    after: [
      { t: "Your toddler isn\u2019t" },
      { t: "a bigger baby." },
      { t: "They can negotiate. Delay. Protest.", em: true },
      { t: "And have very strong opinions", em: true },
      { t: "about all of it.", em: true },
    ],
    afterNote: "That\u2019s why toddler sleep means looking at both when sleep is happening and what happens around it.",
  },
  ending: {
    lines: [
      { t: "Bedtime doesn\u2019t have" },
      { t: "to feel like a two-hour" },
      { t: "negotiation every night.", em: true },
    ],
    sub: "Let\u2019s look at what is actually happening in your house.",
  },
  deepLink: { href: "/toddler-sleep-consultant", label: "Explore toddler sleep support" },
  ctas: [
    { type: "book", label: "Book my complimentary sleep assessment" },
    { type: "tell", label: "Tell Andrea what bedtime looks like" },
    { type: "text", label: "Text Andrea" },
  ],
};
